class QuickAddManager {
  constructor() {
    this.bindEvents();
  }

  bindEvents() {
    // Update selector to target popover instead of sidebar
    document
      .querySelectorAll('[data-popover-target="quick-add"], [data-sidebar-target="quick-add"]')
      .forEach((button) => {
        button.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();

          // If button has popover target, prioritize popover and prevent sidebar
          if (button.hasAttribute('data-popover-target')) {
            // Stop the event from reaching other listeners (like sidebar)
            e.stopImmediatePropagation();
          }

          const productCard = e.target.closest('[data-product-handle]');
          if (!productCard) {
            return;
          }

          const productHandle = productCard.dataset.productHandle;
          const productId = productCard.dataset.productId;

          if (productHandle || productId) {
            await this.loadQuickAdd(productHandle, productId);
          }
        });
      });
  }

  async loadQuickAdd(productHandle, productId) {
    try {
      // Use the global popover function if available
      if (window.openProductPopover) {
        window.openProductPopover(productHandle, productId);
      } else {
        console.error('QuickAdd: Product popover function not available');
        // Fallback to old sidebar method if popover not available
        await this.loadQuickAddSidebar(productHandle);
      }
    } catch (error) {
      console.error('QuickAdd: Error loading quick add:', error);
    }
  }

  // Keep the old sidebar method as fallback
  async loadQuickAddSidebar(productHandle) {
    try {
      const template = document.getElementById('sidebar-quick-add-template');
      if (!template) {
        return;
      }

      const content = template.content.cloneNode(true);
      const sidebarContent = document.getElementById('sidebar-content');
      if (!sidebarContent) {
        console.error('QuickAdd: Sidebar content not found');
        return;
      }

      sidebarContent.innerHTML = '';
      sidebarContent.appendChild(content);

      const sectionResponse = await fetch(
        `${window.Shopify.routes.root}products/${productHandle}?sections=sidebar-product`,
      );
      const sectionData = await sectionResponse.json();

      if (!sectionData['sidebar-product']) {
        console.error('QuickAdd: Section data not found');
        return;
      }

      const quickAddInner = sidebarContent.querySelector('.quick-add-inner');
      quickAddInner.innerHTML = sectionData['sidebar-product'];

      const scripts = Array.from(quickAddInner.querySelectorAll('script'));
      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        if (!oldScript.src) {
          newScript.textContent = oldScript.textContent;
        }
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });

      // Check product limit and update button state
      await this.checkProductLimit(quickAddInner);

      // Open sidebar
      if (window.sidebar) {
        window.sidebar.open('quick-add', true);
      }
    } catch (error) {
      console.error('QuickAdd: Error loading quick add sidebar:', error);
    }
  }

  async checkProductLimit(container) {
    try {
      const productInfo = container.querySelector('product-info');
      const productForm = container.querySelector('product-form');

      if (!productInfo || !productForm) {
        return;
      }

      const productId = productInfo.dataset.productId;

      if (!productId) {
        return;
      }

      const quantityLimitElement = container.querySelector('[data-product-quantity-limit]');

      const quantityLimit = quantityLimitElement
        ? parseInt(quantityLimitElement.dataset.productQuantityLimit, 10)
        : null;

      if (!quantityLimit) {
        return;
      }
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();
      const itemsWithThisProduct = cart.items.filter((item) => item.product_id.toString() === productId);
      const totalInCart = itemsWithThisProduct.reduce((sum, item) => sum + item.quantity, 0);
      const limitReached = totalInCart >= quantityLimit;
      const submitButton = productForm.querySelector('[type="submit"]');

      if (submitButton) {
        if (limitReached) {
          if (typeof productForm.toggleSubmitButton === 'function') {
            productForm.toggleSubmitButton(true, window.variantStrings.inCart);
          } else {
            submitButton.setAttribute('disabled', 'disabled');
            const buttonText = submitButton.querySelector('span');
            if (buttonText) {
              buttonText.textContent = window.variantStrings.inCart;
            }
          }
        }
      }

      // Add event listener for variant changes to maintain limit check
      const variantSelects = container.querySelector('variant-selects');

      if (variantSelects) {
        variantSelects.addEventListener('change', () => {
          if (limitReached) {
            if (typeof productForm.toggleSubmitButton === 'function') {
              productForm.toggleSubmitButton(true, window.variantStrings.inCart);
            } else {
              submitButton.setAttribute('disabled', 'disabled');
              const buttonText = submitButton.querySelector('span');
              if (buttonText) {
                buttonText.textContent = window.variantStrings.inCart;
              }
            }
          }
        });
      }
    } catch (error) {
      console.error('QuickAdd: Error checking product limit:', error);
    }
  }

  getOptionValues(product, optionIndex) {
    const values = new Set();
    product.variants.forEach((variant) => {
      if (variant.options[optionIndex]) {
        values.add(variant.options[optionIndex]);
      }
    });
    return Array.from(values);
  }

  initVariantSelector(container, product) {
    const variantIdInput = container.querySelector('[data-variant-id]');
    const optionInputs = container.querySelectorAll('[data-option-position]');

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const selectedOptions = Array.from({ length: product.options.length }, (_, index) => {
          const selected = container.querySelector(`input[name="option-${index}"]:checked`);
          return selected ? selected.value : null;
        });

        const matchingVariant = product.variants.find((variant) =>
          variant.options.every((option, index) => option === selectedOptions[index]),
        );

        if (matchingVariant) {
          variantIdInput.value = matchingVariant.id;
        }
      });
    });
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(cents / 100);
  }
}

// Initialize immediately
document.addEventListener('DOMContentLoaded', () => {
  new QuickAddManager();
});
